import type { GitHunk } from "gitpod-next-api/supervisor/v1/environmentopts_pb";
import { useMemo, useState, type FC } from "react";
import { PatchDiff } from "@pierre/diffs/react";
import { registerCustomTheme, type SelectedLineRange, type DiffLineAnnotation } from "@pierre/diffs";
import { convertHunksToPatch, computeContentHashForLines } from "@/routes/environments/code-changes/diff-utils";
import { InlineCommentAnnotation } from "@/routes/environments/code-changes/InlineCommentAnnotation";
import type { ReviewComment } from "@/components/ai/review/types";
import pierreLightBase from "@/routes/environments/code-changes/themes/pierre-light.json";
import pierreDarkBase from "@/routes/environments/code-changes/themes/pierre-dark.json";
import { useTheme } from "@/hooks/use-theme";

// Surface primary colors from index.css
// Light: --surface-primary: var(--gray-200) = 246 246 246 = #f6f6f6
// Dark: --surface-primary: var(--gray-900) = 26 26 26 = #1a1a1a

registerCustomTheme("ona-light", () =>
    Promise.resolve({
        ...pierreLightBase,
        name: "ona-light",
        type: "light" as const,
        colors: {
            ...pierreLightBase.colors,
            "editor.background": "#f6f6f6",
        },
    }),
);

registerCustomTheme("ona-dark", () =>
    Promise.resolve({
        ...pierreDarkBase,
        name: "ona-dark",
        type: "dark" as const,
        colors: {
            ...pierreDarkBase.colors,
            "editor.background": "#1a1a1a",
        },
    }),
);

export type LineSelection = {
    startLine: number;
    endLine: number;
    startIndex: number;
    endIndex: number;
    side?: "deletions" | "additions";
};

type AnnotationMetadata = {
    reviewComment?: ReviewComment;
    startLine: number;
    endLine: number;
};

export type GitDiffViewProps = {
    hunks: GitHunk[];
    filePath?: string;
    onLineSelect?: (selection: LineSelection) => void;
    comments?: ReviewComment[];
    onAddComment?: (
        content: string,
        startLine: number,
        endLine: number,
        side?: "deletions" | "additions",
        contentHash?: string,
    ) => void;
    onUpdateComment?: (reviewComment: ReviewComment, newContent: string) => void;
    onDeleteComment?: (reviewComment: ReviewComment) => void;
    onSetCommentThumbsDown?: (reviewComment: ReviewComment, thumbsDown: boolean) => void;
};

const GitDiffView: FC<GitDiffViewProps> = ({
    hunks,
    filePath = "file",
    comments,
    onAddComment,
    onUpdateComment,
    onDeleteComment,
    onSetCommentThumbsDown,
}) => {
    const [commentInputAnnotation, setCommentInputAnnotation] = useState<DiffLineAnnotation<AnnotationMetadata> | null>(
        null,
    );
    const { effectiveTheme } = useTheme();
    const patch = useMemo(() => convertHunksToPatch(hunks, filePath), [hunks, filePath]);

    const handleLineSelectionEnd = (range: SelectedLineRange | null) => {
        if (range) {
            setCommentInputAnnotation({
                side: range.endSide ?? range.side ?? "additions",
                lineNumber: range.end,
                metadata: {
                    startLine: range.start,
                    endLine: range.end,
                },
            });
        }
    };

    const handleCommentSubmit = (content: string) => {
        if (commentInputAnnotation?.metadata) {
            const { startLine, endLine } = commentInputAnnotation.metadata;
            const side = commentInputAnnotation.side;
            // Compute content hash for outdated detection
            const contentHash = computeContentHashForLines(hunks, startLine, endLine, side) ?? undefined;
            onAddComment?.(content, startLine, endLine, side, contentHash);
            setCommentInputAnnotation(null);
        }
    };

    const handleCommentCancel = () => {
        setCommentInputAnnotation(null);
    };

    // Create line annotations for comments and pending comment input
    const lineAnnotations = useMemo<DiffLineAnnotation<AnnotationMetadata>[]>(() => {
        const annotations: DiffLineAnnotation<AnnotationMetadata>[] = [];

        // map comments to line annotations
        if (comments) {
            for (const comment of comments) {
                annotations.push({
                    side: comment.side ?? "additions",
                    lineNumber: comment.endLine,
                    metadata: {
                        reviewComment: comment,
                        startLine: comment.startLine,
                        endLine: comment.endLine,
                    },
                });
            }
        }

        if (commentInputAnnotation) {
            annotations.push(commentInputAnnotation);
        }

        return annotations;
    }, [comments, commentInputAnnotation]);

    const handleCommentUpdate = (comment: ReviewComment) => (newContent: string) => {
        onUpdateComment?.(comment, newContent);
    };

    const handleCommentDelete = (comment: ReviewComment) => () => {
        onDeleteComment?.(comment);
    };

    const renderAnnotation = (annotation: DiffLineAnnotation<AnnotationMetadata>) => {
        const { startLine, endLine, reviewComment } = annotation.metadata;

        // Check if comment is outdated by comparing stored hash with current content hash
        let isOutdated = false;
        if (reviewComment?.contentHash) {
            const currentHash = computeContentHashForLines(
                hunks,
                startLine,
                endLine,
                reviewComment.side ?? "additions",
            );
            isOutdated = currentHash !== null && currentHash !== reviewComment.contentHash;
        }

        return (
            <InlineCommentAnnotation
                startLine={startLine}
                endLine={endLine}
                defaultComment={reviewComment?.content ?? ""}
                // when no review comment is associated with this annotation
                // it means this is a draft comment, so should be default editable
                defaultEditable={!reviewComment}
                isOutdated={isOutdated}
                author={reviewComment?.author}
                commentId={reviewComment?.id}
                filePath={filePath}
                thumbsDown={reviewComment?.thumbsDown}
                onSubmit={reviewComment ? handleCommentUpdate(reviewComment) : handleCommentSubmit}
                onCancel={handleCommentCancel}
                onDelete={reviewComment ? handleCommentDelete(reviewComment) : undefined}
                onThumbsDown={
                    reviewComment && onSetCommentThumbsDown
                        ? (thumbsDown) => onSetCommentThumbsDown(reviewComment, thumbsDown)
                        : undefined
                }
            />
        );
    };

    if (!patch) {
        return null;
    }

    return (
        <PatchDiff<AnnotationMetadata>
            patch={patch}
            options={{
                theme: { dark: "ona-dark", light: "ona-light" },
                themeType: effectiveTheme,
                diffStyle: "unified",
                diffIndicators: "bars",
                hunkSeparators: "simple",
                expandUnchanged: false,
                disableFileHeader: true,
                overflow: "scroll",
                enableLineSelection: !!onAddComment,
                onLineSelectionEnd: onAddComment ? handleLineSelectionEnd : undefined,
            }}
            // if there is comment input annotation, pass undefined to let component handle selected line highlights
            // otherwise, pass null to clear all line highlights
            // this is needed because cancelling command inputs don't clear the line highlights
            // even though lineAnnotations is []
            // passing null when there is no comment input ensures that there is no line highlights
            // and that cancelling clears the highlights
            selectedLines={commentInputAnnotation ? undefined : null}
            lineAnnotations={lineAnnotations}
            renderAnnotation={renderAnnotation}
            // Styling via CSS variables per https://diffs.com/docs#styling
            style={
                {
                    "--diffs-font-family": "var(--font-family-mono)",
                    "--diffs-gap-block": "0px",
                } as React.CSSProperties
            }
        />
    );
};

export { GitDiffView };
